import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneWithSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import type { AxialCoord } from '@/types/hex.types';
import { axialToPixel, mirrorCoord } from '@/hex/HexCoord';
import { UnitMeshType } from './UnitDefinitions';

/**
 * Manages 3D rendering of units on the hex grid
 */
export class UnitRenderer {
  private scene: THREE.Scene;
  private parentGroup: THREE.Group | null = null;
  private unitMeshes: Map<string, THREE.Group> = new Map();
  private healthBars: Map<string, { background: THREE.Mesh; fill: THREE.Mesh }> = new Map();
  private geometries: Map<UnitMeshType, THREE.BufferGeometry[]> = new Map();
  private gltfLoader: GLTFLoader;
  private modelCache: Map<string, THREE.Group> = new Map();
  private loadingModels: Map<string, Promise<THREE.Group>> = new Map();
  private animationMixers: Map<string, THREE.AnimationMixer> = new Map();
  private animationClips: Map<string, { walk?: THREE.AnimationClip; idle?: THREE.AnimationClip; attack?: THREE.AnimationClip; death?: THREE.AnimationClip }> = new Map();
  private currentAnimations: Map<string, THREE.AnimationAction> = new Map();
  private dyingUnits: Set<string> = new Set(); // Track units playing death animation
  private attackIndicators: Set<THREE.Group> = new Set();
  private modelContainers: Map<string, THREE.Group> = new Map(); // Just the 3D models, no UI
  private damageNumbers: Set<THREE.Sprite> = new Set();
  private mergeHighlightedUnits: Map<string, number> = new Map(); // meshId -> animationId

  // Health bar constants
  private static readonly HEALTH_BAR_WIDTH = 0.5;
  private static readonly HEALTH_BAR_HEIGHT = 0.06;
  private static readonly HEALTH_BAR_Y = 0.9;

  // Team colors
  private static readonly TEAM_COLORS = {
    1: 0x3b82f6, // Blue for player 1
    2: 0xef4444, // Red for player 2
  };

  // Outline settings for cartoon shader
  private static readonly OUTLINE_THICKNESS = 0.03;
  private static readonly OUTLINE_COLOR = 0x000000;
  private outlineMaterial: THREE.ShaderMaterial;

  // Mirroring state for multiplayer (local player always at bottom)
  private mirrorEnabled = false;
  private localPlayerId: 1 | 2 | null = null;

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    this.gltfLoader = new GLTFLoader();
    this.outlineMaterial = this.createOutlineMaterial();
    this.createGeometries();
  }

  /**
   * Create the shader material for cartoon outlines
   * Uses vertex extrusion along normals to create the outline effect
   */
  private createOutlineMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        outlineThickness: { value: UnitRenderer.OUTLINE_THICKNESS },
        outlineColor: { value: new THREE.Color(UnitRenderer.OUTLINE_COLOR) },
      },
      vertexShader: `
        uniform float outlineThickness;

        void main() {
          vec3 pos = position + normal * outlineThickness;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 outlineColor;

        void main() {
          gl_FragColor = vec4(outlineColor, 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: true,
    });
  }

  /**
   * Set the parent group for unit meshes (should match hex grid group)
   */
  setParentGroup(group: THREE.Group): void {
    this.parentGroup = group;
  }

  /**
   * Create reusable geometries for each unit mesh type
   */
  private createGeometries(): void {
    // Soldier - humanoid shape (cube body + sphere head)
    this.geometries.set(UnitMeshType.Soldier, [
      new THREE.BoxGeometry(0.4, 0.6, 0.3), // Body
      new THREE.SphereGeometry(0.15, 8, 6), // Head
    ]);

    // Beast - quadruped shape
    this.geometries.set(UnitMeshType.Beast, [
      new THREE.BoxGeometry(0.5, 0.3, 0.3), // Body
      new THREE.SphereGeometry(0.12, 8, 6), // Head
    ]);

    // Mage - robed figure with staff
    this.geometries.set(UnitMeshType.Mage, [
      new THREE.ConeGeometry(0.25, 0.7, 6), // Robe
      new THREE.SphereGeometry(0.12, 8, 6), // Head
      new THREE.CylinderGeometry(0.02, 0.02, 0.6, 4), // Staff
    ]);

    // Assassin - slim figure
    this.geometries.set(UnitMeshType.Assassin, [
      new THREE.BoxGeometry(0.25, 0.65, 0.2), // Body
      new THREE.SphereGeometry(0.1, 8, 6), // Head
    ]);

    // Golem - large bulky figure
    this.geometries.set(UnitMeshType.Golem, [
      new THREE.BoxGeometry(0.6, 0.7, 0.5), // Body
      new THREE.BoxGeometry(0.4, 0.25, 0.35), // Head
    ]);

    // Archer - humanoid with bow indication
    this.geometries.set(UnitMeshType.Archer, [
      new THREE.BoxGeometry(0.35, 0.55, 0.25), // Body
      new THREE.SphereGeometry(0.12, 8, 6), // Head
      new THREE.TorusGeometry(0.15, 0.02, 4, 8, Math.PI), // Bow
    ]);
  }

  /**
   * Create a unit mesh
   */
  createUnitMesh(
    entityId: string,
    meshType: UnitMeshType,
    unitColor: number,
    teamId: number,
    starLevel: number,
    coord: AxialCoord,
    modelPath?: string,
    modelScale?: number
  ): string {
    const meshId = `unit_${entityId}`;
    const group = new THREE.Group();
    const modelContainer = new THREE.Group(); // Container for 3D model only (no UI)
    group.add(modelContainer);

    const teamColor = UnitRenderer.TEAM_COLORS[teamId as 1 | 2] ?? 0xffffff;
    const scale = this.getScaleForStarLevel(starLevel);

    // Create materials (MeshToonMaterial for cel-shading)
    const bodyMaterial = new THREE.MeshToonMaterial({
      color: unitColor,
    });

    const teamMaterial = new THREE.MeshToonMaterial({
      color: teamColor,
      emissive: teamColor,
      emissiveIntensity: 0.2,
    });

    // Store mesh ID early so animations can access it
    group.userData.meshId = meshId;

    // If model path provided, load GLB model
    if (modelPath) {
      this.loadAndAddModel(modelContainer, modelPath, modelScale ?? 1.0, teamColor, teamId, meshId);
    } else {
      // Build mesh based on type (fallback)
      switch (meshType) {
        case UnitMeshType.Soldier:
          this.buildSoldier(modelContainer, bodyMaterial, teamMaterial);
          break;
        case UnitMeshType.Beast:
          this.buildBeast(modelContainer, bodyMaterial, teamMaterial);
          break;
        case UnitMeshType.Mage:
          this.buildMage(modelContainer, bodyMaterial, teamMaterial);
          break;
        case UnitMeshType.Assassin:
          this.buildAssassin(modelContainer, bodyMaterial, teamMaterial);
          break;
        case UnitMeshType.Golem:
          this.buildGolem(modelContainer, bodyMaterial, teamMaterial);
          break;
        case UnitMeshType.Archer:
          this.buildArcher(modelContainer, bodyMaterial, teamMaterial);
          break;
      }
    }

    // Store model container for outline post-processing
    this.modelContainers.set(meshId, modelContainer);

    // Add star indicators
    this.addStarIndicators(group, starLevel, teamColor);

    // Add health bar
    this.addHealthBar(group, meshId, teamColor);

    // Apply scale
    group.scale.setScalar(scale);

    // Position on hex (apply mirroring for visual position)
    const visualCoord = this.mirrorEnabled ? mirrorCoord(coord) : coord;
    const pixel = axialToPixel(visualCoord);
    group.position.set(pixel.x, 0.3, pixel.y);

    // Store reference (serverCoord is the original, non-mirrored coordinate)
    group.userData.entityId = entityId;
    group.userData.meshId = meshId;
    group.userData.serverCoord = coord;
    group.userData.teamId = teamId;

    this.unitMeshes.set(meshId, group);

    // Add to parent group if set (to inherit grid centering), otherwise add to scene
    if (this.parentGroup) {
      this.parentGroup.add(group);
    } else {
      this.scene.add(group);
    }

    return meshId;
  }

  /**
   * Load a GLB model and add it to the group
   */
  private loadAndAddModel(
    group: THREE.Group,
    modelPath: string,
    modelScale: number,
    teamColor: number,
    teamId: number,
    meshId: string
  ): void {
    const fullPath = `/models/${modelPath}`;

    // Check cache first
    const cached = this.modelCache.get(fullPath);
    if (cached) {
      const clone = cloneWithSkeleton(cached) as THREE.Group;
      this.setupLoadedModel(clone, modelScale, teamColor, teamId);
      this.setupAnimations(meshId, clone, cached.userData.animationClips);
      group.add(clone);
      return;
    }

    // Check if already loading
    const loading = this.loadingModels.get(fullPath);
    if (loading) {
      loading.then((loadedModel) => {
        const clone = cloneWithSkeleton(loadedModel) as THREE.Group;
        this.setupLoadedModel(clone, modelScale, teamColor, teamId);
        this.setupAnimations(meshId, clone, loadedModel.userData.animationClips);
        group.add(clone);
      });
      return;
    }

    // Start loading
    const loadPromise = new Promise<THREE.Group>((resolve, reject) => {
      this.gltfLoader.load(
        fullPath,
        (gltf) => {
          const model = gltf.scene;

          // Store animations if available
          if (gltf.animations && gltf.animations.length > 0) {
            const clips: { walk?: THREE.AnimationClip; idle?: THREE.AnimationClip; attack?: THREE.AnimationClip; death?: THREE.AnimationClip } = {};

            console.log(`[UnitRenderer] Found ${gltf.animations.length} animations in ${fullPath}:`);
            for (const clip of gltf.animations) {
              console.log(`  - "${clip.name}"`);
              const nameLower = clip.name.toLowerCase();
              if (nameLower.includes('walk') || nameLower.includes('run')) {
                clips.walk = clip;
                console.log(`    → Using as WALK animation`);
              } else if (nameLower.includes('idle') || nameLower.includes('stand')) {
                clips.idle = clip;
                console.log(`    → Using as IDLE animation`);
              } else if (nameLower.includes('attack') || nameLower.includes('hit') || nameLower.includes('strike') || nameLower.includes('punch') || nameLower.includes('slash')) {
                clips.attack = clip;
                console.log(`    → Using as ATTACK animation`);
              } else if (nameLower.includes('fail') || nameLower.includes('fall') || nameLower.includes('death') || nameLower.includes('die') || nameLower.includes('dead')) {
                clips.death = clip;
                console.log(`    → Using as DEATH animation`);
              }
            }

            // If no specific animations found, use the first animation for both
            if (!clips.walk && !clips.idle && gltf.animations.length > 0) {
              console.log(`  No walk/idle animations detected by name, using first animation for both`);
              clips.walk = gltf.animations[0];
              clips.idle = gltf.animations[0];
            }
            // If only idle animation found, use it for walk too
            else if (clips.idle && !clips.walk) {
              console.log(`  No walk animation, using idle animation for walk`);
              clips.walk = clips.idle;
            }

            // Store clips with the model path as key
            model.userData.animationClips = clips;
            console.log(`Stored animations for ${fullPath}:`, {
              walk: clips.walk?.name,
              idle: clips.idle?.name
            });
          }

          this.modelCache.set(fullPath, model);
          this.loadingModels.delete(fullPath);
          resolve(model);
        },
        undefined,
        (error) => {
          console.error(`Failed to load model ${fullPath}:`, error);
          this.loadingModels.delete(fullPath);
          reject(error);
        }
      );
    });

    this.loadingModels.set(fullPath, loadPromise);

    loadPromise.then((loadedModel) => {
      const clone = cloneWithSkeleton(loadedModel) as THREE.Group;
      this.setupLoadedModel(clone, modelScale, teamColor, teamId);
      this.setupAnimations(meshId, clone, loadedModel.userData.animationClips);
      group.add(clone);
    });
  }

  /**
   * Setup a loaded model with proper scale and shadows
   */
  private setupLoadedModel(
    model: THREE.Group,
    modelScale: number,
    _teamColor: number,
    teamId: number
  ): void {
    // Reset any existing transforms on the model
    model.scale.set(1, 1, 1);
    model.position.set(0, 0, 0);
    // Rotate to face opponent
    // Player 1 faces up toward Player 2, Player 2 faces down toward Player 1
    let facingRotation: number;
    if (this.mirrorEnabled && this.localPlayerId !== null) {
      // Mirrored: local player faces up, opponent faces down
      facingRotation = teamId === this.localPlayerId ? Math.PI / 2 : -Math.PI / 2;
    } else {
      // Normal: player 1 faces up, player 2 faces down
      facingRotation = teamId === 1 ? Math.PI / 2 : -Math.PI / 2;
    }
    model.rotation.set(0, facingRotation, 0);
    model.updateMatrixWorld(true);

    // Calculate bounding box to normalize size
    const box = new THREE.Box3().setFromObject(model);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Guard against zero or invalid dimensions
    if (maxDim <= 0 || !isFinite(maxDim)) {
      console.warn('Model has invalid dimensions, using default scale');
      model.scale.setScalar(modelScale);
      return;
    }

    // Normalize to ~0.8 units and apply custom scale
    const normalizedScale = (0.8 / maxDim) * modelScale;
    model.scale.setScalar(normalizedScale);

    // Recalculate bounding box after scaling
    box.setFromObject(model);
    const center = box.getCenter(new THREE.Vector3());

    // Center horizontally and place on ground
    model.position.x = -center.x;
    model.position.z = -center.z;
    model.position.y = -box.min.y;

    // Setup shadows and convert materials to MeshToonMaterial for cel-shading
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;

        // Convert MeshStandardMaterial to MeshToonMaterial
        if (child.material instanceof THREE.MeshStandardMaterial) {
          const oldMat = child.material;
          const toonMat = new THREE.MeshToonMaterial({
            color: oldMat.color,
            map: oldMat.map,
            emissive: oldMat.emissive,
            emissiveIntensity: oldMat.emissiveIntensity,
            emissiveMap: oldMat.emissiveMap,
            normalMap: oldMat.normalMap,
            alphaMap: oldMat.alphaMap,
            transparent: oldMat.transparent,
            opacity: oldMat.opacity,
            side: oldMat.side,
          });
          child.material = toonMat;
          oldMat.dispose();
        } else if (Array.isArray(child.material)) {
          child.material = child.material.map((mat) => {
            if (mat instanceof THREE.MeshStandardMaterial) {
              const toonMat = new THREE.MeshToonMaterial({
                color: mat.color,
                map: mat.map,
                emissive: mat.emissive,
                emissiveIntensity: mat.emissiveIntensity,
                emissiveMap: mat.emissiveMap,
                normalMap: mat.normalMap,
                alphaMap: mat.alphaMap,
                transparent: mat.transparent,
                opacity: mat.opacity,
                side: mat.side,
              });
              mat.dispose();
              return toonMat;
            }
            return mat;
          });
        }
      }
    });
  }

  /**
   * Setup animations for a loaded model
   */
  private setupAnimations(
    meshId: string,
    model: THREE.Group,
    clips?: { walk?: THREE.AnimationClip; idle?: THREE.AnimationClip; attack?: THREE.AnimationClip; death?: THREE.AnimationClip }
  ): void {
    if (!clips || (!clips.walk && !clips.idle)) {
      console.warn(`[UnitRenderer] No animations available for ${meshId}, unit will be static`);
      return;
    }

    console.log(`Setting up animations for ${meshId}:`, {
      hasWalk: !!clips.walk,
      hasIdle: !!clips.idle,
      hasAttack: !!clips.attack,
      hasDeath: !!clips.death
    });

    // Create animation mixer
    const mixer = new THREE.AnimationMixer(model);
    this.animationMixers.set(meshId, mixer);
    this.animationClips.set(meshId, clips);

    // Start with idle animation if available
    if (clips.idle) {
      const action = mixer.clipAction(clips.idle);
      action.play();
      this.currentAnimations.set(meshId, action);
      console.log(`Started idle animation for ${meshId}`);
    } else {
      console.warn(`[UnitRenderer] No idle animation for ${meshId}, unit will be static when not moving`);
    }
  }

  /**
   * Play walk or idle animation based on movement state
   */
  private playAnimation(meshId: string, isMoving: boolean): void {
    const mixer = this.animationMixers.get(meshId);
    const clips = this.animationClips.get(meshId);
    const currentAction = this.currentAnimations.get(meshId);

    if (!mixer || !clips) return;

    const targetClip = isMoving ? clips.walk : clips.idle;
    if (!targetClip) return;

    // Check if we need to switch animations
    if (!currentAction || currentAction.getClip() !== targetClip) {
      console.log(`Switching animation for ${meshId}: ${isMoving ? 'WALK' : 'IDLE'}`);

      // Fade out current animation
      if (currentAction) {
        currentAction.fadeOut(0.2);
      }

      // Fade in new animation
      const newAction = mixer.clipAction(targetClip);
      newAction.reset().fadeIn(0.2).play();
      this.currentAnimations.set(meshId, newAction);
    }
  }

  /**
   * Play attack animation once, then return to idle/walk
   */
  playAttackAnimation(meshId: string): void {
    const mixer = this.animationMixers.get(meshId);
    const clips = this.animationClips.get(meshId);
    const currentAction = this.currentAnimations.get(meshId);
    const group = this.unitMeshes.get(meshId);

    if (!mixer || !clips) {
      // No animation system at all - use visual fallback
      console.warn(`[UnitRenderer] No animation mixer for ${meshId}, using visual attack fallback`);
      if (group) {
        this.playAttackFallback(group);
      }
      return;
    }

    if (!clips.attack) {
      // No attack animation - use visual fallback
      console.warn(`[UnitRenderer] No attack animation for ${meshId}, using visual fallback`);
      if (group) {
        this.playAttackFallback(group);
      }
      return;
    }

    console.log(`[UnitRenderer] Playing attack animation "${clips.attack.name}" for ${meshId}`);

    // Store the current animation state to return to after attack
    const previousClip = currentAction?.getClip();

    // Stop current animation
    if (currentAction) {
      currentAction.fadeOut(0.1);
    }

    // Play attack animation once
    const attackAction = mixer.clipAction(clips.attack);
    attackAction.reset();
    attackAction.setLoop(THREE.LoopOnce, 1);
    attackAction.clampWhenFinished = false; // Don't stay on last frame
    attackAction.fadeIn(0.1);
    attackAction.play();

    this.currentAnimations.set(meshId, attackAction);

    // Listen for animation completion to return to previous state
    const onFinished = (event: any) => {
      if (event.action === attackAction) {
        mixer.removeEventListener('finished', onFinished);
        console.log(`[UnitRenderer] Attack animation finished for ${meshId}, returning to previous state`);

        // Return to previous animation (idle or walk)
        if (previousClip && (previousClip === clips.idle || previousClip === clips.walk)) {
          const returnAction = mixer.clipAction(previousClip);
          returnAction.reset().fadeIn(0.2).play();
          this.currentAnimations.set(meshId, returnAction);
        } else if (clips.idle) {
          // Default to idle if previous state unknown
          const idleAction = mixer.clipAction(clips.idle);
          idleAction.reset().fadeIn(0.2).play();
          this.currentAnimations.set(meshId, idleAction);
        }
      }
    };
    mixer.addEventListener('finished', onFinished);
  }

  /**
   * Visual fallback for attack when no animation is available
   * Creates a quick scale pulse effect
   */
  private playAttackFallback(group: THREE.Group): void {
    const originalScale = group.scale.clone();
    const duration = 200; // ms
    const startTime = performance.now();
    const scaleMultiplier = 1.15;

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Ease out bounce effect: scale up then back down
      const t = progress < 0.5 ? progress * 2 : (1 - progress) * 2;
      const scale = 1 + (scaleMultiplier - 1) * t;

      group.scale.set(
        originalScale.x * scale,
        originalScale.y * scale,
        originalScale.z * scale
      );

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Restore original scale
        group.scale.copy(originalScale);
      }
    };

    requestAnimationFrame(animate);
  }

  /**
   * Show a brief attack indicator (arrow) from attacker to target
   */
  showAttackIndicator(attackerMeshId: string, targetMeshId: string, duration: number = 200): void {
    const attackerGroup = this.unitMeshes.get(attackerMeshId);
    const targetGroup = this.unitMeshes.get(targetMeshId);

    if (!attackerGroup || !targetGroup) return;

    // Get world positions
    const attackerPos = new THREE.Vector3();
    const targetPos = new THREE.Vector3();
    attackerGroup.getWorldPosition(attackerPos);
    targetGroup.getWorldPosition(targetPos);

    // Raise the indicator height
    attackerPos.y += 0.5;
    targetPos.y += 0.5;

    // Calculate direction and distance
    const direction = new THREE.Vector3().subVectors(targetPos, attackerPos);
    const distance = direction.length();
    direction.normalize();

    // Create indicator group
    const indicatorGroup = new THREE.Group();

    // Create arrow line (cylinder)
    const lineLength = distance * 0.7; // Shorter than full distance
    const lineGeom = new THREE.CylinderGeometry(0.03, 0.03, lineLength, 6);
    const lineMat = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      transparent: true,
      opacity: 0.9,
    });
    const line = new THREE.Mesh(lineGeom, lineMat);

    // Position line starting from attacker
    const midPoint = new THREE.Vector3().addVectors(
      attackerPos,
      direction.clone().multiplyScalar(lineLength / 2 + 0.2)
    );
    line.position.copy(midPoint);

    // Rotate to point toward target
    line.quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      direction
    );

    indicatorGroup.add(line);

    // Create arrowhead (cone)
    const arrowGeom = new THREE.ConeGeometry(0.08, 0.15, 6);
    const arrowMat = new THREE.MeshBasicMaterial({
      color: 0xffaa00,
      transparent: true,
      opacity: 0.9,
    });
    const arrow = new THREE.Mesh(arrowGeom, arrowMat);

    // Position arrowhead at end of line
    const arrowPos = new THREE.Vector3().addVectors(
      attackerPos,
      direction.clone().multiplyScalar(lineLength + 0.2)
    );
    arrow.position.copy(arrowPos);
    arrow.quaternion.setFromUnitVectors(
      new THREE.Vector3(0, 1, 0),
      direction
    );

    indicatorGroup.add(arrow);

    // Add to scene (not to parentGroup, to avoid centering offset issues)
    this.scene.add(indicatorGroup);
    this.attackIndicators.add(indicatorGroup);

    // Animate fade out
    const startTime = performance.now();
    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Fade out opacity
      const opacity = 0.9 * (1 - progress);
      lineMat.opacity = opacity;
      arrowMat.opacity = opacity;

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Clean up
        this.scene.remove(indicatorGroup);
        this.attackIndicators.delete(indicatorGroup);
        lineGeom.dispose();
        lineMat.dispose();
        arrowGeom.dispose();
        arrowMat.dispose();
      }
    };

    animate();
  }

  /**
   * Show floating damage number above a unit
   */
  showDamageNumber(targetMeshId: string, damage: number, isCritical: boolean = false): void {
    const targetGroup = this.unitMeshes.get(targetMeshId);
    if (!targetGroup) return;

    // Create canvas for the damage text
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = 128;
    canvas.height = 64;

    // Determine color based on damage amount
    // Low: white/light, Medium: yellow/orange, High: red, Crit: purple/magenta
    let color: string;
    let fontSize = 32;

    if (isCritical) {
      color = '#ff44ff'; // Magenta for crits
      fontSize = 40;
    } else if (damage >= 50) {
      color = '#ff4444'; // Red for high damage
      fontSize = 36;
    } else if (damage >= 25) {
      color = '#ffaa44'; // Orange for medium damage
    } else if (damage >= 10) {
      color = '#ffff44'; // Yellow for low-medium
    } else {
      color = '#ffffff'; // White for low damage
    }

    // Draw text with outline for visibility
    context.font = `bold ${fontSize}px Arial`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';

    // Black outline
    context.strokeStyle = '#000000';
    context.lineWidth = 4;
    const text = isCritical ? `${damage}!` : String(damage);
    context.strokeText(text, 64, 32);

    // Colored fill
    context.fillStyle = color;
    context.fillText(text, 64, 32);

    // Create sprite texture
    const texture = new THREE.CanvasTexture(canvas);
    texture.needsUpdate = true;

    const material = new THREE.SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
    });

    const sprite = new THREE.Sprite(material);
    sprite.scale.set(0.8, 0.4, 1);

    // Position above the unit
    const worldPos = new THREE.Vector3();
    targetGroup.getWorldPosition(worldPos);
    sprite.position.copy(worldPos);
    sprite.position.y += 1.2;

    // Add small random offset to prevent overlap
    sprite.position.x += (Math.random() - 0.5) * 0.3;

    this.scene.add(sprite);
    this.damageNumbers.add(sprite);

    // Animate floating up and fading out
    const startTime = performance.now();
    const duration = 800;
    const startY = sprite.position.y;

    const animateDamage = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Float up
      sprite.position.y = startY + progress * 0.5;

      // Fade out (start fading at 50% progress)
      if (progress > 0.5) {
        material.opacity = 1 - (progress - 0.5) * 2;
      }

      // Scale down slightly at the end
      const scale = 1 - progress * 0.3;
      sprite.scale.set(0.8 * scale, 0.4 * scale, 1);

      if (progress < 1) {
        requestAnimationFrame(animateDamage);
      } else {
        // Clean up
        this.scene.remove(sprite);
        this.damageNumbers.delete(sprite);
        material.dispose();
        texture.dispose();
      }
    };

    animateDamage();
  }

  private buildSoldier(
    group: THREE.Group,
    bodyMat: THREE.Material,
    teamMat: THREE.Material
  ): void {
    const geometries = this.geometries.get(UnitMeshType.Soldier)!;

    // Body
    const body = new THREE.Mesh(geometries[0], bodyMat);
    body.position.y = 0.3;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);

    // Head
    const head = new THREE.Mesh(geometries[1], teamMat);
    head.position.y = 0.7;
    head.castShadow = true;
    group.add(head);

    // Team banner (small flag on back)
    const bannerGeom = new THREE.PlaneGeometry(0.15, 0.2);
    const banner = new THREE.Mesh(bannerGeom, teamMat);
    banner.position.set(0, 0.55, -0.2);
    banner.rotation.x = -0.3;
    group.add(banner);
  }

  private buildBeast(
    group: THREE.Group,
    bodyMat: THREE.Material,
    teamMat: THREE.Material
  ): void {
    const geometries = this.geometries.get(UnitMeshType.Beast)!;

    // Body
    const body = new THREE.Mesh(geometries[0], bodyMat);
    body.position.y = 0.25;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);

    // Head
    const head = new THREE.Mesh(geometries[1], teamMat);
    head.position.set(0.25, 0.35, 0);
    head.castShadow = true;
    group.add(head);

    // Legs (4 small cylinders)
    const legGeom = new THREE.CylinderGeometry(0.04, 0.04, 0.2, 4);
    const legPositions = [
      { x: -0.15, z: 0.1 },
      { x: -0.15, z: -0.1 },
      { x: 0.15, z: 0.1 },
      { x: 0.15, z: -0.1 },
    ];
    for (const pos of legPositions) {
      const leg = new THREE.Mesh(legGeom, bodyMat);
      leg.position.set(pos.x, 0.1, pos.z);
      leg.castShadow = true;
      group.add(leg);
    }
  }

  private buildMage(
    group: THREE.Group,
    bodyMat: THREE.Material,
    teamMat: THREE.Material
  ): void {
    const geometries = this.geometries.get(UnitMeshType.Mage)!;

    // Robe (cone)
    const robe = new THREE.Mesh(geometries[0], bodyMat);
    robe.position.y = 0.35;
    robe.castShadow = true;
    robe.receiveShadow = true;
    group.add(robe);

    // Head
    const head = new THREE.Mesh(geometries[1], teamMat);
    head.position.y = 0.8;
    head.castShadow = true;
    group.add(head);

    // Staff
    const staffMat = new THREE.MeshToonMaterial({
      color: 0x8b4513,
    });
    const staff = new THREE.Mesh(geometries[2], staffMat);
    staff.position.set(0.25, 0.4, 0);
    staff.castShadow = true;
    group.add(staff);

    // Staff orb (glowing)
    const orbGeom = new THREE.SphereGeometry(0.06, 8, 6);
    const orbMat = new THREE.MeshToonMaterial({
      color: teamMat instanceof THREE.MeshToonMaterial ? teamMat.color : 0xffffff,
      emissive: teamMat instanceof THREE.MeshToonMaterial ? teamMat.color : 0xffffff,
      emissiveIntensity: 0.5,
    });
    const orb = new THREE.Mesh(orbGeom, orbMat);
    orb.position.set(0.25, 0.75, 0);
    group.add(orb);
  }

  private buildAssassin(
    group: THREE.Group,
    bodyMat: THREE.Material,
    teamMat: THREE.Material
  ): void {
    const geometries = this.geometries.get(UnitMeshType.Assassin)!;

    // Slim body
    const body = new THREE.Mesh(geometries[0], bodyMat);
    body.position.y = 0.33;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);

    // Hooded head
    const head = new THREE.Mesh(geometries[1], teamMat);
    head.position.y = 0.75;
    head.castShadow = true;
    group.add(head);

    // Hood
    const hoodGeom = new THREE.ConeGeometry(0.12, 0.15, 4);
    const hood = new THREE.Mesh(hoodGeom, bodyMat);
    hood.position.y = 0.8;
    hood.rotation.x = 0.3;
    group.add(hood);

    // Daggers
    const daggerGeom = new THREE.ConeGeometry(0.03, 0.2, 4);
    const daggerMat = new THREE.MeshToonMaterial({
      color: 0xc0c0c0,
    });
    const dagger1 = new THREE.Mesh(daggerGeom, daggerMat);
    dagger1.position.set(0.2, 0.3, 0);
    dagger1.rotation.z = -Math.PI / 4;
    group.add(dagger1);

    const dagger2 = new THREE.Mesh(daggerGeom, daggerMat);
    dagger2.position.set(-0.2, 0.3, 0);
    dagger2.rotation.z = Math.PI / 4;
    group.add(dagger2);
  }

  private buildGolem(
    group: THREE.Group,
    bodyMat: THREE.Material,
    teamMat: THREE.Material
  ): void {
    const geometries = this.geometries.get(UnitMeshType.Golem)!;

    // Massive body
    const body = new THREE.Mesh(geometries[0], bodyMat);
    body.position.y = 0.35;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);

    // Head (box for golem)
    const head = new THREE.Mesh(geometries[1], teamMat);
    head.position.y = 0.8;
    head.castShadow = true;
    group.add(head);

    // Arms
    const armGeom = new THREE.BoxGeometry(0.15, 0.4, 0.15);
    const leftArm = new THREE.Mesh(armGeom, bodyMat);
    leftArm.position.set(-0.4, 0.35, 0);
    leftArm.castShadow = true;
    group.add(leftArm);

    const rightArm = new THREE.Mesh(armGeom, bodyMat);
    rightArm.position.set(0.4, 0.35, 0);
    rightArm.castShadow = true;
    group.add(rightArm);

    // Glowing runes (small emissive spots)
    const runeGeom = new THREE.CircleGeometry(0.05, 6);
    const runeMat = new THREE.MeshToonMaterial({
      color: teamMat instanceof THREE.MeshToonMaterial ? teamMat.color : 0xffffff,
      emissive: teamMat instanceof THREE.MeshToonMaterial ? teamMat.color : 0xffffff,
      emissiveIntensity: 0.8,
    });
    const runePositions = [
      { x: 0, y: 0.5, z: 0.26 },
      { x: -0.15, y: 0.3, z: 0.26 },
      { x: 0.15, y: 0.3, z: 0.26 },
    ];
    for (const pos of runePositions) {
      const rune = new THREE.Mesh(runeGeom, runeMat);
      rune.position.set(pos.x, pos.y, pos.z);
      group.add(rune);
    }
  }

  private buildArcher(
    group: THREE.Group,
    bodyMat: THREE.Material,
    teamMat: THREE.Material
  ): void {
    const geometries = this.geometries.get(UnitMeshType.Archer)!;

    // Body
    const body = new THREE.Mesh(geometries[0], bodyMat);
    body.position.y = 0.28;
    body.castShadow = true;
    body.receiveShadow = true;
    group.add(body);

    // Head
    const head = new THREE.Mesh(geometries[1], teamMat);
    head.position.y = 0.65;
    head.castShadow = true;
    group.add(head);

    // Bow
    const bowMat = new THREE.MeshToonMaterial({
      color: 0x8b4513,
    });
    const bow = new THREE.Mesh(geometries[2], bowMat);
    bow.position.set(0.25, 0.35, 0);
    bow.rotation.z = Math.PI / 2;
    bow.rotation.y = Math.PI / 2;
    bow.castShadow = true;
    group.add(bow);

    // Quiver on back
    const quiverGeom = new THREE.CylinderGeometry(0.05, 0.05, 0.3, 6);
    const quiver = new THREE.Mesh(quiverGeom, bowMat);
    quiver.position.set(0, 0.4, -0.18);
    quiver.rotation.x = 0.2;
    group.add(quiver);
  }

  /**
   * Add star level indicators above the unit
   */
  private addStarIndicators(
    group: THREE.Group,
    starLevel: number,
    teamColor: number
  ): void {
    const starGeom = new THREE.OctahedronGeometry(0.05, 0);
    const starMat = new THREE.MeshToonMaterial({
      color: 0xffd700,
      emissive: 0xffd700,
      emissiveIntensity: 0.5,
    });

    const startX = -((starLevel - 1) * 0.1) / 2;
    for (let i = 0; i < starLevel; i++) {
      const star = new THREE.Mesh(starGeom, starMat);
      star.position.set(startX + i * 0.1, 1.0, 0);
      star.rotation.y = Math.PI / 4;
      group.add(star);
    }

    // Add team color ring at base
    const ringGeom = new THREE.RingGeometry(0.25, 0.3, 16);
    const ringMat = new THREE.MeshBasicMaterial({
      color: teamColor,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.7,
    });
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.01;
    group.add(ring);
  }

  /**
   * Add health bar above the unit
   */
  private addHealthBar(group: THREE.Group, meshId: string, teamColor: number): void {
    const width = UnitRenderer.HEALTH_BAR_WIDTH;
    const height = UnitRenderer.HEALTH_BAR_HEIGHT;

    // Background (dark)
    const bgGeom = new THREE.PlaneGeometry(width, height);
    const bgMat = new THREE.MeshBasicMaterial({
      color: 0x333333,
      side: THREE.DoubleSide,
    });
    const background = new THREE.Mesh(bgGeom, bgMat);
    background.position.set(0, UnitRenderer.HEALTH_BAR_Y, 0);
    background.rotation.x = -Math.PI / 6; // Tilt towards camera
    group.add(background);

    // Health fill (colored based on team)
    const fillGeom = new THREE.PlaneGeometry(width - 0.02, height - 0.02);
    const fillMat = new THREE.MeshBasicMaterial({
      color: teamColor,
      side: THREE.DoubleSide,
    });
    const fill = new THREE.Mesh(fillGeom, fillMat);
    fill.position.set(0, UnitRenderer.HEALTH_BAR_Y, 0.001); // Slightly in front
    fill.rotation.x = -Math.PI / 6;
    group.add(fill);

    // Store reference for updates
    this.healthBars.set(meshId, { background, fill });
  }

  /**
   * Update health bar display
   */
  updateHealthBar(meshId: string, healthPercent: number): void {
    const healthBar = this.healthBars.get(meshId);
    if (!healthBar) return;

    const { fill } = healthBar;
    const width = UnitRenderer.HEALTH_BAR_WIDTH - 0.02;
    const clampedPercent = Math.max(0, Math.min(1, healthPercent));

    // Scale the fill bar horizontally
    fill.scale.x = clampedPercent;

    // Offset to keep it aligned to the left
    fill.position.x = -(width * (1 - clampedPercent)) / 2;

    // Change color based on health (green -> yellow -> red)
    const mat = fill.material as THREE.MeshBasicMaterial;
    if (clampedPercent > 0.6) {
      mat.color.setHex(0x22c55e); // Green
    } else if (clampedPercent > 0.3) {
      mat.color.setHex(0xeab308); // Yellow
    } else {
      mat.color.setHex(0xef4444); // Red
    }
  }

  /**
   * Get scale multiplier for star level
   */
  private getScaleForStarLevel(starLevel: number): number {
    const baseScale = 2.0;
    const scalePerStar = 0.2;
    return baseScale + (starLevel - 1) * scalePerStar;
  }

  /**
   * Update unit position
   */
  updatePosition(meshId: string, coord: AxialCoord): void {
    const group = this.unitMeshes.get(meshId);
    if (group) {
      // Apply mirroring for visual position
      const visualCoord = this.mirrorEnabled ? mirrorCoord(coord) : coord;
      const pixel = axialToPixel(visualCoord);
      group.position.set(pixel.x, 0.3, pixel.y);
      group.userData.serverCoord = coord;
    }
  }

  /**
   * Smoothly move unit to new position
   */
  setTargetPosition(meshId: string, coord: AxialCoord): void {
    const group = this.unitMeshes.get(meshId);
    if (group) {
      // Apply mirroring for visual position
      const visualCoord = this.mirrorEnabled ? mirrorCoord(coord) : coord;
      const pixel = axialToPixel(visualCoord);
      group.userData.targetPosition = new THREE.Vector3(pixel.x, 0.3, pixel.y);
      group.userData.serverCoord = coord;
    }
  }

  /**
   * Update mesh animations (call in render loop)
   */
  update(deltaTime: number): void {
    const moveSpeed = 3.0;

    for (const group of this.unitMeshes.values()) {
      const meshId = group.userData.meshId as string;
      const target = group.userData.targetPosition as THREE.Vector3 | undefined;
      const isMoving = !!target;

      // Update position if moving
      if (target) {
        const current = group.position;
        const direction = new THREE.Vector3()
          .subVectors(target, current)
          .normalize();
        const distance = current.distanceTo(target);

        if (distance > 0.01) {
          const moveDistance = Math.min(moveSpeed * deltaTime, distance);
          current.add(direction.multiplyScalar(moveDistance));
        } else {
          current.copy(target);
          group.userData.targetPosition = undefined;
        }
      }

      // Update animation based on movement state (skip if unit is dying)
      if (meshId && !this.dyingUnits.has(meshId)) {
        this.playAnimation(meshId, isMoving);
      }

      // Always update animation mixer (including for dying units)
      if (meshId) {
        const mixer = this.animationMixers.get(meshId);
        if (mixer) {
          mixer.update(deltaTime);

          // Lock model position to prevent root motion from moving the character
          // The group handles movement, not the animation
          group.children.forEach((child) => {
            if (child.type === 'Group') {
              child.position.set(0, 0, 0);
            }
          });
        }
      }
    }
  }

  /**
   * Remove a unit mesh
   */
  removeUnitMesh(meshId: string): void {
    const group = this.unitMeshes.get(meshId);
    if (group) {
      // Remove from parent group or scene
      if (this.parentGroup) {
        this.parentGroup.remove(group);
      } else {
        this.scene.remove(group);
      }
      // Dispose materials
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.material instanceof THREE.Material) {
            child.material.dispose();
          } else if (Array.isArray(child.material)) {
            child.material.forEach((m) => m.dispose());
          }
        }
      });
      this.unitMeshes.delete(meshId);
      this.healthBars.delete(meshId);
      this.modelContainers.delete(meshId);

      // Clean up animations
      const mixer = this.animationMixers.get(meshId);
      if (mixer) {
        mixer.stopAllAction();
        this.animationMixers.delete(meshId);
      }
      this.animationClips.delete(meshId);
      this.currentAnimations.delete(meshId);
    }
  }

  /**
   * Get mesh by ID
   */
  getMesh(meshId: string): THREE.Group | undefined {
    return this.unitMeshes.get(meshId);
  }

  /**
   * Get all model containers (for outline post-processing)
   * Returns only the 3D models, excluding UI elements like health bars and stars
   */
  getAllMeshes(): THREE.Group[] {
    return Array.from(this.modelContainers.values());
  }

  /**
   * Highlight a unit (for selection)
   */
  highlightUnit(meshId: string, highlight: boolean): void {
    const group = this.unitMeshes.get(meshId);
    if (group) {
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mat = child.material;
          if (mat instanceof THREE.MeshToonMaterial || mat instanceof THREE.MeshStandardMaterial) {
            mat.emissiveIntensity = highlight ? 0.5 : 0.2;
          }
        }
      });
    }
  }

  /**
   * Set merge highlight on a unit (pulsing green glow to indicate upgrade potential)
   */
  setMergeHighlight(meshId: string, enabled: boolean): void {
    const group = this.unitMeshes.get(meshId);
    if (!group) return;

    // If disabling, stop the animation and restore original state
    if (!enabled) {
      const animationId = this.mergeHighlightedUnits.get(meshId);
      if (animationId !== undefined) {
        cancelAnimationFrame(animationId);
        this.mergeHighlightedUnits.delete(meshId);
      }

      // Restore original emissive color and intensity
      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mat = child.material;
          if (mat instanceof THREE.MeshToonMaterial || mat instanceof THREE.MeshStandardMaterial) {
            mat.emissive.setHex(mat.userData.originalEmissive ?? 0x000000);
            mat.emissiveIntensity = 0.2;
          }
        }
      });
      return;
    }

    // Already highlighted, skip
    if (this.mergeHighlightedUnits.has(meshId)) return;

    // Store original emissive colors and set to green
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mat = child.material;
        if (mat instanceof THREE.MeshToonMaterial || mat instanceof THREE.MeshStandardMaterial) {
          if (mat.userData.originalEmissive === undefined) {
            mat.userData.originalEmissive = mat.emissive.getHex();
          }
          mat.emissive.setHex(0x44ff44); // Green glow
        }
      }
    });

    // Animate pulsing glow
    const startTime = performance.now();
    const animate = () => {
      const elapsed = performance.now() - startTime;
      const pulse = 0.3 + Math.sin(elapsed * 0.008) * 0.25; // Pulse between 0.05 and 0.55

      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mat = child.material;
          if (mat instanceof THREE.MeshToonMaterial || mat instanceof THREE.MeshStandardMaterial) {
            mat.emissiveIntensity = pulse;
          }
        }
      });

      const animationId = requestAnimationFrame(animate);
      this.mergeHighlightedUnits.set(meshId, animationId);
    };

    const animationId = requestAnimationFrame(animate);
    this.mergeHighlightedUnits.set(meshId, animationId);
  }

  /**
   * Clear all merge highlights
   */
  clearAllMergeHighlights(): void {
    for (const meshId of this.mergeHighlightedUnits.keys()) {
      this.setMergeHighlight(meshId, false);
    }
  }

  /**
   * Play death animation
   */
  playDeathAnimation(meshId: string, onComplete: () => void): void {
    const group = this.unitMeshes.get(meshId);
    if (!group) {
      console.log(`[UnitRenderer] playDeathAnimation: No mesh found for ${meshId}`);
      onComplete();
      return;
    }

    const mixer = this.animationMixers.get(meshId);
    const clips = this.animationClips.get(meshId);
    const currentAction = this.currentAnimations.get(meshId);

    // Debug: Log available animations
    console.log(`[UnitRenderer] Death requested for ${meshId}. Available clips:`, {
      hasWalk: !!clips?.walk,
      hasIdle: !!clips?.idle,
      hasAttack: !!clips?.attack,
      hasDeath: !!clips?.death,
      walkName: clips?.walk?.name,
      idleName: clips?.idle?.name,
      attackName: clips?.attack?.name,
      deathName: clips?.death?.name
    });

    // If death animation is available, use it
    if (mixer && clips?.death) {
      console.log(`[UnitRenderer] Playing death animation "${clips.death.name}" for ${meshId}`);

      // Stop current animation
      if (currentAction) {
        currentAction.fadeOut(0.1);
      }

      // Play death animation once
      const deathAction = mixer.clipAction(clips.death);
      deathAction.reset();
      deathAction.setLoop(THREE.LoopOnce, 1);
      deathAction.clampWhenFinished = true; // Stay on last frame
      deathAction.fadeIn(0.1);
      deathAction.play();

      this.currentAnimations.set(meshId, deathAction);

      // Mark unit as dying to prevent animation system from overriding
      this.dyingUnits.add(meshId);

      // Track if animation completed to avoid double-calling onComplete
      let animationCompleted = false;

      const completeDeathAnimation = () => {
        if (animationCompleted) return;
        animationCompleted = true;
        mixer.removeEventListener('finished', onFinished);
        console.log(`[UnitRenderer] Death animation finished for ${meshId}`);
        this.fadeOutUnit(group, onComplete, meshId);
      };

      // Listen for animation completion
      const onFinished = (event: any) => {
        if (event.action === deathAction) {
          completeDeathAnimation();
        }
      };
      mixer.addEventListener('finished', onFinished);

      // Safety timeout in case animation event never fires (prevents soft-lock)
      const animationDuration = clips.death.duration * 1000; // Convert to ms
      const safetyTimeout = Math.max(animationDuration + 500, 3000); // At least 3 seconds or animation duration + 500ms
      setTimeout(() => {
        if (!animationCompleted) {
          console.warn(`[UnitRenderer] Death animation timeout for ${meshId}, forcing completion`);
          completeDeathAnimation();
        }
      }, safetyTimeout);

    } else {
      // Fallback: simple fade out animation
      console.warn(`[UnitRenderer] No death animation for ${meshId}, using fade-out fallback`);
      this.fadeOutUnit(group, onComplete, meshId);
    }
  }

  /**
   * Fade out a unit (used after death animation or as fallback)
   */
  private fadeOutUnit(group: THREE.Group, onComplete: () => void, meshId?: string): void {
    const duration = 500;
    const startTime = performance.now();

    const animate = () => {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      group.scale.setScalar(1 - progress * 0.5);
      group.position.y = 0.3 - progress * 0.3;

      group.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mat = child.material;
          if (mat instanceof THREE.MeshToonMaterial || mat instanceof THREE.MeshStandardMaterial) {
            mat.opacity = 1 - progress;
            mat.transparent = true;
          }
        }
      });

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Hide the mesh instead of removing it (for resurrection)
        group.visible = false;

        // Clean up dying state
        if (meshId) {
          this.dyingUnits.delete(meshId);
        }

        onComplete();
      }
    };

    animate();
  }

  /**
   * Show a unit mesh (for resurrection after death)
   */
  showUnit(meshId: string, starLevel: number = 1): void {
    const group = this.unitMeshes.get(meshId);
    if (!group) return;

    // Reset visibility
    group.visible = true;

    // Reset scale based on star level
    const scale = this.getScaleForStarLevel(starLevel);
    group.scale.setScalar(scale);
    group.position.y = 0.3;

    // Reset material opacity
    group.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mat = child.material;
        if (mat instanceof THREE.MeshToonMaterial || mat instanceof THREE.MeshStandardMaterial) {
          mat.opacity = 1;
          mat.transparent = false;
        }
      }
    });
  }

  /**
   * Enable or disable coordinate mirroring for multiplayer
   * When Player 2, we mirror positions so their territory appears at the bottom
   */
  setMirroring(localPlayerId: 1 | 2 | null, enabled: boolean): void {
    this.localPlayerId = localPlayerId;
    this.mirrorEnabled = enabled;
    // Update all existing unit positions
    this.updateAllUnitPositions();
  }

  /**
   * Check if mirroring is enabled
   */
  isMirrorEnabled(): boolean {
    return this.mirrorEnabled;
  }

  /**
   * Get the local player ID for mirroring
   */
  getLocalPlayerId(): 1 | 2 | null {
    return this.localPlayerId;
  }

  /**
   * Update all unit positions based on mirroring state
   */
  private updateAllUnitPositions(): void {
    for (const group of this.unitMeshes.values()) {
      const serverCoord = group.userData.serverCoord as AxialCoord | undefined;
      const teamId = group.userData.teamId as number | undefined;

      if (serverCoord) {
        const visualCoord = this.mirrorEnabled ? mirrorCoord(serverCoord) : serverCoord;
        const pixel = axialToPixel(visualCoord);
        group.position.set(pixel.x, 0.3, pixel.y);
      }

      // Update facing direction for loaded models
      if (teamId !== undefined && this.localPlayerId !== null) {
        // Update rotation on all model children
        group.traverse((child) => {
          if (child.type === 'Group' && child !== group) {
            let facingRotation: number;
            if (this.mirrorEnabled) {
              facingRotation = teamId === this.localPlayerId ? 0 : Math.PI;
            } else {
              facingRotation = teamId === 1 ? Math.PI : 0;
            }
            child.rotation.y = facingRotation;
          }
        });
      }
    }
  }

  /**
   * Clean up all resources
   */
  dispose(): void {
    for (const meshId of this.unitMeshes.keys()) {
      this.removeUnitMesh(meshId);
    }

    for (const geometries of this.geometries.values()) {
      for (const geom of geometries) {
        geom.dispose();
      }
    }
    this.geometries.clear();

    // Dispose outline material
    this.outlineMaterial.dispose();
  }
}
